import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect } from "react";
import { useWishlistStore } from "@/store/wishlistStore";
import { supabase } from "@/integrations/supabase/client";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { ScrollToTop } from "@/hooks/useScrollToTop";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { LanguageProvider } from "@/i18n";
import { AlertToastProvider } from "@/contexts/AlertToastContext";
import { AlertToastSync } from "@/components/AlertToastSync";
import { useLocation } from "react-router-dom";

// Lazy load non-critical overlay components
const WishlistLoginPrompt = lazy(() => import("@/components/WishlistLoginPrompt").then(m => ({ default: m.WishlistLoginPrompt })));
const CookieBanner = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookieBanner })));
const CookiePreferencesModal = lazy(() => import("@/components/CookieConsent").then(m => ({ default: m.CookiePreferencesModal })));
const JewelryAdvisorBot = lazy(() => import("@/components/JewelryAdvisor").then(m => ({ default: m.JewelryAdvisorBot })));

// Eager load Index for fastest homepage render
import Index from "./pages/Index";

// Lazy load all other routes for code splitting
const Shop = lazy(() => import("./pages/Shop"));
const Category = lazy(() => import("./pages/Category"));
const Product = lazy(() => import("./pages/Product"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Returns = lazy(() => import("./pages/Returns"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const TradeIn = lazy(() => import("./pages/TradeIn"));
const RingSizeGuide = lazy(() => import("./pages/RingSizeGuide"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Shipping = lazy(() => import("./pages/Shipping"));
const FAQ = lazy(() => import("./pages/FAQ"));
const GiftCard = lazy(() => import("./pages/GiftCard"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AdminContactMessages = lazy(() => import("./pages/AdminContactMessages"));
const AdminRepairs = lazy(() => import("./pages/AdminRepairs"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Cart = lazy(() => import("./pages/Cart"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const TVDisplay = lazy(() => import("./pages/TVDisplay"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));

const queryClient = new QueryClient();

const AppContent = () => {
  const loadWishlist = useWishlistStore((state) => state.loadWishlist);
  const preload = useGlobalPreload((state) => state.preload);
  const location = useLocation();
  const isTVPage = location.pathname === "/tv";

  // Apply language from URL ?lang= parameter on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get("lang");
    if (urlLang && ["he", "en", "ru", "ar"].includes(urlLang)) {
      import("@/components/GoogleTranslateSwitcher").then(({ triggerGoogleTranslate }) => {
        triggerGoogleTranslate(urlLang === "he" ? "" : urlLang);
      });
    }
  }, []);

  useEffect(() => {
    preload();
  }, [preload]);

  useEffect(() => {
    const loadUserData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        loadWishlist();
      }
    };

    loadUserData();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        loadWishlist();
      } else if (event === 'SIGNED_OUT') {
        loadWishlist();
      }
    });

    return () => subscription.unsubscribe();
  }, [loadWishlist]);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={null}>
        {!isTVPage && <WishlistLoginPrompt />}
        {!isTVPage && <CookieBanner />}
        {!isTVPage && <CookiePreferencesModal />}
        {!isTVPage && <JewelryAdvisorBot />}
      </Suspense>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/collections/:category" element={<Category />} />
          <Route path="/collections/:collection/products/:handle" element={<Product />} />
          <Route path="/products/:handle" element={<Product />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/trade-in" element={<TradeIn />} />
          <Route path="/ring-size-guide" element={<RingSizeGuide />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/gift-card" element={<GiftCard />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
          <Route path="/admin/repairs" element={<AdminRepairs />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/sitemap.xml" element={<Sitemap />} />
          <Route path="/tv" element={<TVDisplay />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <CookieConsentProvider>
            <AlertToastProvider>
              <AlertToastSync />
              <Toaster />
              <BrowserRouter>
                <AppContent />
              </BrowserRouter>
            </AlertToastProvider>
          </CookieConsentProvider>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
